import { create } from 'zustand';
import { chatApi, adminChatApi } from '../api/chatApi';
import { TYPING_TTL_MS } from '../constants/reactions';
import { parseCreatedAtMs } from 'utils/time';

const clientMsgId = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return `c-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const bySeq = (list) => [...list].sort((a, b) => (a.seq || 0) - (b.seq || 0));

export const sortConversations = (list) => [...list].sort((a, b) => {
    if (Boolean(a.pinned) !== Boolean(b.pinned)) {
        return a.pinned ? -1 : 1;
    }
    return parseCreatedAtMs(b.lastMessageAt) - parseCreatedAtMs(a.lastMessageAt);
});

const mergeById = (list, item) => {
    const next = list.filter((row) => row.id !== item.id);
    next.push(item);
    return sortConversations(next);
};

const mergeMessage = (list, message) => {
    const byClient = list.find((row) => row.clientMsgId && row.clientMsgId === message.clientMsgId);
    const byId = list.find((row) => row.id === message.id);
    const without = list.filter((row) => row !== byClient && row !== byId);
    without.push(message);
    return bySeq(without);
};

const applyMessageEdit = (list, seq, body, editedAt) =>
    (list || []).map((row) => {
        let next = row.seq === seq ? { ...row, body, editedAt } : row;
        if (next.replyTo?.seq === seq) {
            next = { ...next, replyTo: { ...next.replyTo, body, deleted: false } };
        }
        return next;
    });

const sameId = (left, right) => left != null && right != null && String(left) === String(right);

const threadOf = (messages, conversationId) =>
    messages[conversationId] || messages[String(conversationId)] || [];

export const useChatStore = create((set, get) => ({
    communityId: null,
    selfUserId: null,
    conversations: [],
    messages: {},
    unreadTotal: 0,
    serverTime: null,
    pendingCount: 0,
    introEnabled: false,
    activeConversationId: null,
    loading: false,
    typingByConversation: {},

    setCommunityId: (communityId) => set({ communityId }),
    setSelfUserId: (selfUserId) => set({ selfUserId }),
    setActiveConversationId: (activeConversationId) => set({ activeConversationId }),

    loadConversations: async (communityId) => {
        set({ loading: true, communityId });
        try {
            const conversations = await chatApi.list(communityId);
            const unread = await chatApi.unread(communityId);
            set({
                conversations: sortConversations(conversations || []),
                unreadTotal: unread?.total || 0,
                serverTime: unread?.serverTime || null,
                loading: false,
            });
        } catch {
            set({ conversations: [], unreadTotal: 0, serverTime: null, loading: false });
        }
    },

    loadUnread: async (communityId) => {
        try {
            const unread = await chatApi.unread(communityId);
            set((current) => ({
                unreadTotal: unread?.total || 0,
                serverTime: unread?.serverTime || current.serverTime,
            }));
        } catch {
            set({ unreadTotal: 0 });
        }
    },

    loadPendingCount: async (communityId) => {
        try {
            const data = await adminChatApi.pendingCount(communityId);
            set({ pendingCount: data?.count || 0 });
        } catch {
            set({ pendingCount: 0 });
        }
    },

    loadIntroEnabled: async (communityId) => {
        try {
            const data = await chatApi.introsEnabled(communityId);
            set({ introEnabled: !!data?.enabled });
        } catch {
            set({ introEnabled: false });
        }
    },

    loadHistory: async (communityId, conversationId, beforeSeq) => {
        const page = await chatApi.history(communityId, conversationId, beforeSeq);
        set((state) => ({
            messages: {
                ...state.messages,
                [conversationId]: bySeq(beforeSeq
                    ? [...(page || []), ...(state.messages[conversationId] || [])]
                    : page || []),
            },
        }));
        return page || [];
    },

    catchUp: async (communityId, conversationId, afterSeq) => {
        try {
            const page = await chatApi.afterSeq(communityId, conversationId, afterSeq);
            if (!page?.length) {
                return;
            }
            set((state) => ({
                messages: {
                    ...state.messages,
                    [conversationId]: page.reduce(mergeMessage, state.messages[conversationId] || []),
                },
            }));
        } catch {
            // Keep the local thread; the next successful catch-up or history load will fill gaps.
        }
    },

    send: async (communityId, conversationId, text, selfParticipantId, replyTo) => {
        const id = clientMsgId();
        const optimistic = {
            id,
            seq: Number.MAX_SAFE_INTEGER,
            kind: 'TEXT',
            sender: { kind: 'PARTICIPANT', participantId: selfParticipantId },
            body: text,
            deleted: false,
            clientMsgId: id,
            createdAt: new Date().toISOString(),
            pending: true,
            replyTo: replyTo || undefined,
        };
        set((state) => ({
            messages: {
                ...state.messages,
                [conversationId]: mergeMessage(state.messages[conversationId] || [], optimistic),
            },
            conversations: state.conversations.map((row) =>
                sameId(row.id, conversationId)
                    ? {
                        ...row,
                        lastMessagePreview: text,
                        lastMessageAt: optimistic.createdAt,
                        lastMessageSenderParticipantId: selfParticipantId,
                        lastMessagePending: true,
                    }
                    : row
            ),
        }));
        const saved = await chatApi.send(communityId, conversationId, text, id, replyTo?.seq);
        set((state) => ({
            messages: {
                ...state.messages,
                [conversationId]: mergeMessage(state.messages[conversationId] || [], saved),
            },
            conversations: state.conversations.map((row) =>
                sameId(row.id, conversationId)
                    ? {
                        ...row,
                        lastSeq: saved.seq,
                        lastMessagePreview: saved.body || text,
                        lastMessageSenderParticipantId: selfParticipantId,
                        lastMessagePending: false,
                    }
                    : row
            ),
        }));
        return saved;
    },

    deleteMessage: async (communityId, conversationId, seq) => {
        const snapshot = (get().messages[conversationId] || []).find((row) => row.seq === seq);
        if (!snapshot || snapshot.deleted) {
            return;
        }
        set((state) => ({
            messages: {
                ...state.messages,
                [conversationId]: (state.messages[conversationId] || []).map((row) =>
                    row.seq === seq ? { ...row, body: '', deleted: true } : row
                ),
            },
        }));
        try {
            await chatApi.deleteMessage(communityId, conversationId, seq);
        } catch {
            set((state) => ({
                messages: {
                    ...state.messages,
                    [conversationId]: (state.messages[conversationId] || []).map((row) =>
                        row.seq === seq ? snapshot : row
                    ),
                },
            }));
        }
    },

    editMessage: async (communityId, conversationId, seq, text) => {
        const snapshot = (get().messages[conversationId] || []).find((row) => row.seq === seq);
        if (!snapshot || snapshot.deleted) {
            return;
        }
        const conversation = get().conversations.find((row) => sameId(row.id, conversationId));
        const previewSnapshot = conversation?.lastMessagePreview;
        const isLast = conversation != null && conversation.lastSeq === seq;
        const editedAt = new Date().toISOString();
        set((state) => ({
            messages: {
                ...state.messages,
                [conversationId]: applyMessageEdit(state.messages[conversationId], seq, text, editedAt),
            },
            conversations: state.conversations.map((row) =>
                sameId(row.id, conversationId) && isLast
                    ? { ...row, lastMessagePreview: text }
                    : row
            ),
        }));
        try {
            const saved = await chatApi.editMessage(communityId, conversationId, seq, text);
            set((state) => ({
                messages: {
                    ...state.messages,
                    [conversationId]: applyMessageEdit(
                        state.messages[conversationId],
                        seq,
                        saved.body,
                        saved.editedAt,
                    ),
                },
                conversations: state.conversations.map((row) =>
                    sameId(row.id, conversationId) && isLast
                        ? { ...row, lastMessagePreview: saved.body }
                        : row
                ),
            }));
            return saved;
        } catch {
            set((state) => ({
                messages: {
                    ...state.messages,
                    [conversationId]: (state.messages[conversationId] || []).map((row) => {
                        if (row.seq === seq) {
                            return snapshot;
                        }
                        if (row.replyTo?.seq === seq) {
                            return { ...row, replyTo: { ...row.replyTo, body: snapshot.body } };
                        }
                        return row;
                    }),
                },
                conversations: state.conversations.map((row) =>
                    sameId(row.id, conversationId)
                        ? { ...row, lastMessagePreview: previewSnapshot }
                        : row
                ),
            }));
        }
    },

    markRead: async (communityId, conversationId, seq) => {
        await chatApi.read(communityId, conversationId, seq);
        set((state) => ({
            conversations: state.conversations.map((row) =>
                row.id === conversationId ? { ...row, unreadCount: 0 } : row
            ),
            unreadTotal: Math.max(0, state.unreadTotal - (state.conversations.find((row) => row.id === conversationId)?.unreadCount || 0)),
        }));
    },

    notifyTyping: async (communityId, conversationId) => {
        try {
            await chatApi.typing(communityId, conversationId);
        } catch {
            // Typing is best-effort; a failed ping must not block composing.
        }
    },

    toggleReaction: async (communityId, conversationId, seq, emoji) => {
        const reactions = await chatApi.toggleReaction(communityId, conversationId, seq, emoji);
        set((state) => ({
            messages: {
                ...state.messages,
                [conversationId]: (state.messages[conversationId] || []).map((row) =>
                    row.seq === seq ? { ...row, reactions } : row
                ),
            },
        }));
        return reactions;
    },

    applyRealtime: (envelope) => {
        if (!envelope) {
            return;
        }
        if (envelope.type === 'INTRO_PENDING') {
            set((state) => ({ pendingCount: state.pendingCount + 1 }));
            return;
        }
        if (envelope.type === 'CONVERSATION_CREATED') {
            get().loadConversations(get().communityId);
            return;
        }
        if (envelope.type === 'MESSAGE_NEW' && envelope.conversationId) {
            const state = get();
            const conversationId = envelope.conversationId;
            const viewing = sameId(state.activeConversationId, conversationId);
            const conv = state.conversations.find((row) => sameId(row.id, conversationId));
            const alreadyApplied = conv != null
                && envelope.seq != null
                && conv.lastSeq != null
                && envelope.seq <= conv.lastSeq;
            const clientMsgId = envelope.payload?.clientMsgId;
            const knownMessage = threadOf(state.messages, conversationId).some((row) =>
                (envelope.seq != null && row.seq === envelope.seq)
                || (clientMsgId && row.clientMsgId === clientMsgId)
            );
            const fromSelf = sameId(envelope.userId, state.selfUserId);
            const skipUnread = viewing || alreadyApplied || knownMessage || fromSelf;
            const leftParticipantId = envelope.payload?.kind === 'SYSTEM'
                ? envelope.payload.senderParticipantId
                : null;
            set((current) => ({
                conversations: sortConversations(current.conversations
                    .map((row) =>
                        sameId(row.id, conversationId)
                            ? {
                                ...row,
                                lastMessagePreview: envelope.preview,
                                lastMessageAt: envelope.at || new Date().toISOString(),
                                lastSeq: envelope.seq,
                                lastMessageSenderParticipantId: envelope.payload?.senderParticipantId || null,
                                lastMessagePending: false,
                                unreadCount: skipUnread
                                    ? (viewing ? 0 : row.unreadCount)
                                    : (row.unreadCount || 0) + 1,
                                members: leftParticipantId
                                    ? (row.members || []).filter((member) => !sameId(member.participantId, leftParticipantId))
                                    : row.members,
                            }
                            : row
                    )),
                unreadTotal: skipUnread ? current.unreadTotal : current.unreadTotal + 1,
            }));
            if (viewing && state.communityId && !alreadyApplied) {
                get().catchUp(state.communityId, conversationId, (envelope.seq || 1) - 1);
            } else if (!viewing && state.communityId && !conv) {
                get().loadConversations(state.communityId);
            }
        }
        if (envelope.type === 'MESSAGE_DELETED' && envelope.conversationId) {
            set((state) => ({
                messages: {
                    ...state.messages,
                    [envelope.conversationId]: (state.messages[envelope.conversationId] || []).map((row) =>
                        row.seq === envelope.seq ? { ...row, body: '', deleted: true } : row
                    ),
                },
            }));
        }
        if (envelope.type === 'MESSAGE_EDITED' && envelope.conversationId) {
            const body = envelope.payload?.body;
            const editedAt = envelope.payload?.editedAt;
            set((state) => ({
                messages: {
                    ...state.messages,
                    [envelope.conversationId]: applyMessageEdit(
                        state.messages[envelope.conversationId],
                        envelope.seq,
                        body,
                        editedAt,
                    ),
                },
                conversations: state.conversations.map((row) =>
                    sameId(row.id, envelope.conversationId) && row.lastSeq === envelope.seq
                        ? { ...row, lastMessagePreview: envelope.preview ?? body }
                        : row
                ),
            }));
        }
        if (envelope.type === 'MESSAGE_READ' && envelope.conversationId) {
            const participantId = envelope.payload?.participantId;
            const seq = envelope.seq;
            if (participantId != null && seq != null) {
                set((state) => ({
                    conversations: state.conversations.map((row) => {
                        if (!sameId(row.id, envelope.conversationId)) {
                            return row;
                        }
                        return {
                            ...row,
                            members: (row.members || []).map((member) =>
                                sameId(member.participantId, participantId)
                                    ? { ...member, lastReadSeq: Math.max(member.lastReadSeq || 0, seq) }
                                    : member
                            ),
                        };
                    }),
                }));
            }
        }
        if (envelope.type === 'TYPING' && envelope.conversationId) {
            const participantId = envelope.payload?.participantId;
            if (participantId && !sameId(envelope.userId, get().selfUserId)) {
                set((state) => ({
                    typingByConversation: {
                        ...state.typingByConversation,
                        [envelope.conversationId]: { participantId, until: Date.now() + TYPING_TTL_MS },
                    },
                }));
            }
        }
        if (envelope.type === 'REACTION_UPDATED' && envelope.conversationId && envelope.seq != null) {
            const reactions = envelope.payload?.reactions || [];
            set((state) => ({
                messages: {
                    ...state.messages,
                    [envelope.conversationId]: (state.messages[envelope.conversationId] || []).map((row) =>
                        row.seq === envelope.seq ? { ...row, reactions } : row
                    ),
                },
            }));
        }
    },

    upsertConversation: (conversation) => set((state) => ({
        conversations: mergeById(state.conversations, conversation),
    })),
}));
